import React from 'react';
import PropTypes from 'prop-types';
import { Select, Tag } from 'antd';
import { CheckOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import style from './index.scss';

const { Option } = Select;

/**
 * Scenario selector — the row above the timeline in the updated design:
 *
 *   SCENARIO [1. Account takeover via OTP bypass v]  ‹ 1 / 2 ›
 *          [Transacting to a new location_clone1] [Score 45] [Action: REJECTED]
 *
 * An alert can fire for several reasons; each scenario has its own narrative and
 * its own subset of the events. Rendering ONE timeline at a time is the point —
 * the whole row of nodes changes when you page.
 *
 * TWO CONTROLS, ONE STATE. The dropdown and the ‹ › pager are alternative ways to
 * move the same index, exactly as the design shows. The pager is not decoration:
 * with two scenarios it is one click rather than open-then-pick, and it is the
 * only affordance that says "there is more than one" without being opened.
 *
 * The design numbers the options ("1. …", "2. …") to match the pager, so the two
 * controls always agree about which scenario is which.
 */
const ScenarioBar = ({ scenarios, selectedIndex, onSelect }) => {
  const current = scenarios[selectedIndex];
  const count = scenarios.length;
  if (!current) return null;

  // Clamped, not wrapped. Wrapping (1/2 -> previous -> 2/2) would imply a cycle;
  // the design's arrows are a linear pager, and the ends simply have nowhere to
  // go — which the disabled state already communicates.
  //
  // The clamp lives in the `disabled` props below, NOT in here. A guard on the
  // index was tried and removed: a disabled button never dispatches a click, so
  // it was unreachable, and the container's own handleScenarioSelect already
  // rejects an out-of-range index (`if (!next …) return`) for anything that
  // bypasses the DOM. Two dead layers of the same check read as three defences
  // and test as one.
  const go = (delta) => onSelect(selectedIndex + delta);

  return (
    <div className={style.scenarioBar}>
      {/* "VARIANT", not "SCENARIO" (2026-08-26). The two words named the same
          thing and the code already said so — toScenarioList's header reads "A
          SCENARIO IS A VARIANT — the specific rule that fired". The dropdown has
          listed variant names off `triggered_rule` since the BE settled that
          model on 2026-08-21, so the old label was describing the previous
          contract. The internal names (scenarios, ScenarioBar, onSelect) are
          deliberately NOT renamed here: that is a wide mechanical change across
          the container, the adapter and their tests, and it would bury this
          one-word fix. */}
      <span className={style.scenarioLabel}>VARIANT</span>

      <Select
        className={style.scenarioSelect}
        dropdownClassName={style.scenarioDropdown}
        value={selectedIndex}
        onChange={onSelect}
        size="small"
        /* The design's dropdown is wider than its trigger; without this antd
           clips long scenario names to the trigger width. */
        dropdownMatchSelectWidth={false}
        /* The design marks the current option with a tick in a left gutter
           instead of antd's tinted row — see .scenarioDropdown, which moves
           this icon over and removes the wash. */
        menuItemSelectedIcon={<CheckOutlined />}
        aria-label="Select variant"
      >
        {scenarios.map((s, i) => (
          <Option key={s.id} value={i}>
            {`${i + 1}. ${s.name}`}
          </Option>
        ))}
      </Select>

      <span className={style.scenarioPager}>
        <button
          type="button"
          className={style.pagerButton}
          onClick={() => go(-1)}
          disabled={selectedIndex === 0}
          aria-label="Previous variant"
        >
          <LeftOutlined />
        </button>
        {/* aria-live: paging changes only this text, so a screen-reader user
            would otherwise get no feedback that anything happened. */}
        <span className={style.pagerCount} aria-live="polite">
          {selectedIndex + 1} / {count}
        </span>
        <button
          type="button"
          className={style.pagerButton}
          onClick={() => go(1)}
          disabled={selectedIndex === count - 1}
          aria-label="Next variant"
        >
          <RightOutlined />
        </button>
      </span>

      {/* TYPOLOGY / Score / Action for the SELECTED scenario — the same three
          values the Explanation table shows for that typology, joined from the
          same slices by toScenarioMeta (the scenarios endpoint carries none of
          them). Each is independently null-guarded: an alert can resolve a
          typology but no contribution score, and "All events" resolves none of
          them, so a missing one drops its own tag rather than blanking the row or
          rendering "Score undefined". */}
      <span className={style.scenarioMeta}>
        {/* The TYPOLOGY. The dropdown label is the variant name, so badging the
            variant would repeat the line above; the typology is the context the
            label does not carry, and it is what the design puts here.
            `variant` remains the fallback for the mock fixture, whose entries are
            typology-shaped and carry a separate variant name — the regression
            guard in EventTimeline.scenarios.test.js pins that it keeps a tag. */}
        {current.typology || current.variant ? (
          /* FIN-13565 — the tag clips at 260px so a long typology cannot be
             read. `title` carries the full text on hover, which is the whole
             point of the ellipsis: truncation is only acceptable when the
             untruncated value is reachable.
             A native title rather than antd's Tooltip: this sits inside a row
             that already re-renders on every scenario change, and the browser's
             own tooltip costs nothing and needs no portal. */
          <Tag
            className={style.variantTag}
            title={current.typology || current.variant}>
            {current.typology || current.variant}
          </Tag>
        ) : null}
        {current.score != null ? (
          // 2dp, same as Classic's Explanation table (preventionTable.js), so
          // the two views never disagree on a variant's score (FIN-13725).
          <Tag className={style.scoreTag}>
            Score {Number(current.score).toFixed(2)}
          </Tag>
        ) : null}
        {/* The design labels this one — "Action: REJECTED", not a bare
            "REJECTED" — because unlike the variant and the score the value alone
            does not say what it is. */}
        {/* Configured level colour, as Classic's Explanation (FIN-13724):
            Classic colours the TEXT, so a pale level colour stays readable.
            The pill goes neutral when a colour resolved; unresolved keeps the
            stylesheet's red pill. */}
        {current.action ? (
          <Tag
            className={style.actionTag}
            style={
              current.actionColor
                ? {
                    color: current.actionColor,
                    background: 'transparent',
                    borderColor: current.actionColor
                  }
                : undefined
            }>
            Action: {current.action}
          </Tag>
        ) : null}
      </span>
    </div>
  );
};

ScenarioBar.propTypes = {
  scenarios: PropTypes.array.isRequired,
  selectedIndex: PropTypes.number.isRequired,
  onSelect: PropTypes.func.isRequired
};

export default ScenarioBar;
